import { Button, TextField } from '@cambridgeassessment/cambridge-ui';
import { Collapse, Fade } from '@material-ui/core';
import React, { memo, useCallback, useMemo, useState } from 'react';
import type { ValidationType } from 'src/types';
import type { ValidationRule } from 'src/utils/validator';
import { validationHandler } from 'src/utils/validator';
import AddButton from './AddButton';
import { useStylesAddEmail as useStyles } from './styles';

type Props = {
  disabled?: boolean;
  validationRules?: ValidationRule[];
  onUpdate: (val: string) => void;
};

type InputField = {
  value: string;
  helperText?: string;
  validationType?: ValidationType;
  isValid: boolean;
  showErr: boolean;
};

const AddEmail: React.FC<Props> = ({ disabled, onUpdate, validationRules }) => {
  const classes = useStyles();
  const validator = useMemo(
    () =>
      validationHandler(['required', 'email', 'length128'], validationRules),
    [validationRules]
  );

  const [inProgress, setInProgress] = useState<boolean>(false);
  const [email, setEmail] = useState<InputField>({
    value: '',
    ...validator.validateInput(''),
    showErr: false,
  });

  const handleClickAdd = useCallback(() => {
    setInProgress(true);
  }, []);

  const handleChange = useCallback(
    (e) => {
      const value = e.target.value;
      setEmail((prev) => ({
        ...prev,
        value,
        ...validator.validateInput(value),
      }));
    },
    [validator]
  );

  const handleErrText = useCallback(() => {
    setEmail((prev) => ({
      ...prev,
      showErr: true,
    }));
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const currentEmail = validator.validateInput(
        email.value.toLocaleLowerCase()
      );
      if (currentEmail.isValid) {
        setInProgress(false);
        onUpdate(email.value.toLocaleLowerCase().trim());
        setEmail({
          value: '',
          ...validator.validateInput(''),
          showErr: false,
        });
      }
    },
    [validator, email.value, onUpdate]
  );

  const handleCancel = useCallback(() => {
    setInProgress(false);
    setEmail({
      value: '',
      ...validator.validateInput(''),
      showErr: false,
    });
  }, [validator]);

  return (
    <>
      <Collapse appear={false} in={inProgress}>
        <form onSubmit={handleSubmit} data-testid="addEmail-form">
          <TextField
            className={classes.input}
            id="email"
            value={email.value}
            onChange={handleChange}
            onBlur={handleErrText}
            validationType={email.showErr && !email.isValid ? 'error' : null}
            helperText={email.showErr && !email.isValid && email.helperText}
            label="Email address"
            dataTestId="addEmail"
          />
          <div className={classes.actions}>
            <Button
              disabled={!email.isValid}
              color="primary"
              type="submit"
              data-testid="addEmail-save"
            >
              Save changes
            </Button>

            <Button
              variant="outlined"
              color="primary"
              onClick={handleCancel}
              data-testid="addEmail-cancel"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Collapse>

      <Fade in={!inProgress}>
        <span className={classes.primaryActions}>
          <AddButton disabled={disabled} onClick={handleClickAdd} />
        </span>
      </Fade>
    </>
  );
};

export default memo(AddEmail);
